/**
 * Voice service — CRUD for workspace_voices, memberships, seats, corpus.
 */

import { getSupabaseClient } from '../../lib/supabaseClient';
import type {
  SessionVoiceSeat,
  VoiceCorpusDocument,
  VoiceCorpusKind,
  VoiceCorpusSourceKind,
  VoiceCountingPolicy,
  VoiceMembership,
  VoiceSeatType,
  VoiceStatus,
  WorkspaceVoice,
} from './types';
import { slugifyVoiceName } from './types';

const VOICES = 'workspace_voices';
const MEMBERSHIPS = 'voice_memberships';
const CORPUS = 'voice_corpus_documents';
const SEATS = 'session_voice_seats';

function rowToVoice(row: Record<string, unknown>): WorkspaceVoice {
  return {
    id: row.id as string,
    workspaceId: row.workspace_id as string,
    slug: row.slug as string,
    displayName: row.display_name as string,
    description: (row.description as string | null) ?? null,
    sponsorUserId: row.sponsor_user_id as string,
    standing: 'participant',
    participationSeats: (row.participation_seats as VoiceSeatType[]) ?? [],
    defaultCountingPolicy: (row.default_counting_policy as VoiceCountingPolicy) ?? 'advisory',
    corpusKind: (row.corpus_kind as VoiceCorpusKind | null) ?? null,
    status: (row.status as VoiceStatus) ?? 'active',
    badgeLabel: (row.badge_label as string) ?? 'Voice',
    badgeColor: (row.badge_color as string) ?? 'mustard',
    createdAt: row.created_at as string,
    updatedAt: row.updated_at as string,
  };
}

function rowToMembership(row: Record<string, unknown>): VoiceMembership {
  return {
    id: row.id as string,
    voiceId: row.voice_id as string,
    orgUnitId: row.org_unit_id as string,
    createdAt: row.created_at as string,
  };
}

function rowToCorpus(row: Record<string, unknown>): VoiceCorpusDocument {
  return {
    id: row.id as string,
    voiceId: row.voice_id as string,
    workspaceId: row.workspace_id as string,
    sourceKind: row.source_kind as VoiceCorpusSourceKind,
    externalRef: (row.external_ref as string | null) ?? null,
    title: row.title as string,
    body: (row.body as string) ?? '',
    occurredAt: (row.occurred_at as string | null) ?? null,
    metadata: (row.metadata as Record<string, unknown>) ?? {},
    createdAt: row.created_at as string,
  };
}

function rowToSeat(row: Record<string, unknown>): SessionVoiceSeat {
  return {
    id: row.id as string,
    sessionId: row.session_id as string,
    voiceId: row.voice_id as string,
    countingPolicy: (row.counting_policy as VoiceCountingPolicy) ?? 'advisory',
    seatType: (row.seat_type as VoiceSeatType) ?? 'constituency',
    addedBy: row.added_by as string,
    overrideCap: Boolean(row.override_cap),
    overrideReason: (row.override_reason as string | null) ?? null,
    status: (row.status as 'active' | 'removed') ?? 'active',
    addedAt: row.added_at as string,
    policyChangedBy: (row.policy_changed_by as string | null) ?? null,
    policyChangedAt: (row.policy_changed_at as string | null) ?? null,
  };
}

export async function listWorkspaceVoices(
  workspaceId: string,
): Promise<{ data: WorkspaceVoice[]; error: string | null }> {
  try {
    const supabase = getSupabaseClient();
    const { data, error } = await supabase
      .from(VOICES)
      .select('*')
      .eq('workspace_id', workspaceId)
      .order('display_name', { ascending: true });
    if (error) return { data: [], error: error.message };
    return { data: (data ?? []).map((r) => rowToVoice(r as Record<string, unknown>)), error: null };
  } catch (err) {
    return { data: [], error: err instanceof Error ? err.message : 'Unknown error' };
  }
}

export async function listSponsoredVoices(
  workspaceId: string,
  sponsorUserId: string,
): Promise<{ data: WorkspaceVoice[]; error: string | null }> {
  try {
    const supabase = getSupabaseClient();
    const { data, error } = await supabase
      .from(VOICES)
      .select('*')
      .eq('workspace_id', workspaceId)
      .eq('sponsor_user_id', sponsorUserId)
      .order('display_name', { ascending: true });
    if (error) return { data: [], error: error.message };
    return { data: (data ?? []).map((r) => rowToVoice(r as Record<string, unknown>)), error: null };
  } catch (err) {
    return { data: [], error: err instanceof Error ? err.message : 'Unknown error' };
  }
}

export async function getVoice(
  voiceId: string,
): Promise<{ data: WorkspaceVoice | null; error: string | null }> {
  try {
    const supabase = getSupabaseClient();
    const { data, error } = await supabase.from(VOICES).select('*').eq('id', voiceId).maybeSingle();
    if (error) return { data: null, error: error.message };
    if (!data) return { data: null, error: null };
    return { data: rowToVoice(data as Record<string, unknown>), error: null };
  } catch (err) {
    return { data: null, error: err instanceof Error ? err.message : 'Unknown error' };
  }
}

export interface UpsertVoiceInput {
  workspaceId: string;
  displayName: string;
  slug?: string;
  description?: string | null;
  sponsorUserId: string;
  participationSeats?: VoiceSeatType[];
  corpusKind?: VoiceCorpusKind | null;
  status?: VoiceStatus;
  id?: string;
}

export async function upsertVoice(
  input: UpsertVoiceInput,
): Promise<{ data: WorkspaceVoice | null; error: string | null }> {
  try {
    const supabase = getSupabaseClient();
    const slug = input.slug?.trim() || slugifyVoiceName(input.displayName);
    const payload: Record<string, unknown> = {
      workspace_id: input.workspaceId,
      slug,
      display_name: input.displayName.trim(),
      description: input.description ?? null,
      sponsor_user_id: input.sponsorUserId,
      participation_seats: input.participationSeats ?? ['constituency'],
      default_counting_policy: 'advisory',
      corpus_kind: input.corpusKind ?? null,
      status: input.status ?? 'active',
      updated_at: new Date().toISOString(),
    };
    if (input.id) payload.id = input.id;

    const { data, error } = await supabase
      .from(VOICES)
      .upsert(payload, { onConflict: 'workspace_id,slug' })
      .select()
      .single();
    if (error) return { data: null, error: error.message };
    return { data: rowToVoice(data as Record<string, unknown>), error: null };
  } catch (err) {
    return { data: null, error: err instanceof Error ? err.message : 'Unknown error' };
  }
}

export async function updateVoiceSponsor(
  voiceId: string,
  sponsorUserId: string,
): Promise<{ error: string | null }> {
  try {
    const supabase = getSupabaseClient();
    const { error } = await supabase
      .from(VOICES)
      .update({
        sponsor_user_id: sponsorUserId,
        status: 'active',
        updated_at: new Date().toISOString(),
      })
      .eq('id', voiceId);
    return { error: error?.message ?? null };
  } catch (err) {
    return { error: err instanceof Error ? err.message : 'Unknown error' };
  }
}

export async function listVoiceMemberships(
  voiceId: string,
): Promise<{ data: VoiceMembership[]; error: string | null }> {
  try {
    const supabase = getSupabaseClient();
    const { data, error } = await supabase.from(MEMBERSHIPS).select('*').eq('voice_id', voiceId);
    if (error) return { data: [], error: error.message };
    return { data: (data ?? []).map((r) => rowToMembership(r as Record<string, unknown>)), error: null };
  } catch (err) {
    return { data: [], error: err instanceof Error ? err.message : 'Unknown error' };
  }
}

export async function setVoiceMemberships(
  voiceId: string,
  orgUnitIds: string[],
): Promise<{ error: string | null }> {
  try {
    const supabase = getSupabaseClient();
    await supabase.from(MEMBERSHIPS).delete().eq('voice_id', voiceId);
    if (orgUnitIds.length === 0) return { error: null };
    const rows = orgUnitIds.map((orgUnitId) => ({
      voice_id: voiceId,
      org_unit_id: orgUnitId,
    }));
    const { error } = await supabase.from(MEMBERSHIPS).insert(rows);
    return { error: error?.message ?? null };
  } catch (err) {
    return { error: err instanceof Error ? err.message : 'Unknown error' };
  }
}

export async function listCorpusDocuments(
  voiceId: string,
  limit = 50,
): Promise<{ data: VoiceCorpusDocument[]; error: string | null }> {
  try {
    const supabase = getSupabaseClient();
    const { data, error } = await supabase
      .from(CORPUS)
      .select('*')
      .eq('voice_id', voiceId)
      .order('occurred_at', { ascending: false, nullsFirst: false })
      .limit(limit);
    if (error) return { data: [], error: error.message };
    return { data: (data ?? []).map((r) => rowToCorpus(r as Record<string, unknown>)), error: null };
  } catch (err) {
    return { data: [], error: err instanceof Error ? err.message : 'Unknown error' };
  }
}

/**
 * Phase 2+ hook: live connector ingest. Phase 1 is seed-only; this documents
 * the path without claiming live Zendesk/HubSpot sync.
 */
export async function ingestVoiceCorpusFromConnector(
  _voiceId: string,
  _connectorType: string,
): Promise<{ imported: number; error: string | null }> {
  return {
    imported: 0,
    error: 'Live connector ingest is not available yet. Use seeded corpus documents.',
  };
}

export async function listSessionVoiceSeats(
  sessionId: string,
): Promise<{ data: SessionVoiceSeat[]; error: string | null }> {
  try {
    const supabase = getSupabaseClient();
    const { data, error } = await supabase
      .from(SEATS)
      .select('*')
      .eq('session_id', sessionId)
      .order('added_at', { ascending: true });
    if (error) return { data: [], error: error.message };
    return { data: (data ?? []).map((r) => rowToSeat(r as Record<string, unknown>)), error: null };
  } catch (err) {
    return { data: [], error: err instanceof Error ? err.message : 'Unknown error' };
  }
}

export interface AddSessionVoiceSeatInput {
  sessionId: string;
  voiceId: string;
  addedBy: string;
  seatType?: VoiceSeatType;
  overrideCap?: boolean;
  overrideReason?: string | null;
}

export async function addSessionVoiceSeat(
  input: AddSessionVoiceSeatInput,
): Promise<{ data: SessionVoiceSeat | null; error: string | null }> {
  try {
    const supabase = getSupabaseClient();
    const { data: existing } = await supabase
      .from(SEATS)
      .select('*')
      .eq('session_id', input.sessionId)
      .eq('voice_id', input.voiceId)
      .maybeSingle();

    if (existing) {
      if ((existing as { status?: string }).status === 'removed') {
        const { data, error } = await supabase
          .from(SEATS)
          .update({
            status: 'active',
            // Re-invitation returns to the advisory floor (PRD §4); a fresh
            // facilitator opt-in is required to count again.
            counting_policy: 'advisory',
            policy_changed_by: null,
            policy_changed_at: null,
            override_cap: input.overrideCap ?? false,
            override_reason: input.overrideReason ?? null,
            added_by: input.addedBy,
            added_at: new Date().toISOString(),
          })
          .eq('id', (existing as { id: string }).id)
          .select()
          .single();
        if (error) return { data: null, error: error.message };
        return { data: rowToSeat(data as Record<string, unknown>), error: null };
      }
      return { data: rowToSeat(existing as Record<string, unknown>), error: null };
    }

    const { data, error } = await supabase
      .from(SEATS)
      .insert({
        session_id: input.sessionId,
        voice_id: input.voiceId,
        counting_policy: 'advisory',
        seat_type: input.seatType ?? 'constituency',
        added_by: input.addedBy,
        override_cap: input.overrideCap ?? false,
        override_reason: input.overrideReason ?? null,
        status: 'active',
      })
      .select()
      .single();
    if (error) return { data: null, error: error.message };
    return { data: rowToSeat(data as Record<string, unknown>), error: null };
  } catch (err) {
    return { data: null, error: err instanceof Error ? err.message : 'Unknown error' };
  }
}

export interface SetSeatCountingPolicyInput {
  seatId: string;
  policy: 'advisory' | 'counted';
  /** The facilitator performing the opt-in (PRD §4 attribution). */
  changedBy: string;
}

/**
 * Facilitator opt-in: flip a seat between advisory and counted (PRD §4).
 *
 * Plain table update by design: seat policy is session CONFIGURATION — it
 * decides which lane the Voice's FUTURE contributions land in. It is not an
 * authority transition (no session/cycle/PoR state changes hands), so §2.3a
 * transactional-RPC treatment does not apply. Already-persisted responses
 * keep their audit-stable counting_policy snapshot (migration 601).
 */
export async function setSeatCountingPolicy(
  input: SetSeatCountingPolicyInput,
): Promise<{ data: SessionVoiceSeat | null; error: string | null }> {
  try {
    const supabase = getSupabaseClient();
    const { data, error } = await supabase
      .from(SEATS)
      .update({
        counting_policy: input.policy,
        policy_changed_by: input.changedBy,
        policy_changed_at: new Date().toISOString(),
      })
      .eq('id', input.seatId)
      .select()
      .single();
    if (error) return { data: null, error: error.message };
    return { data: rowToSeat(data as Record<string, unknown>), error: null };
  } catch (err) {
    return { data: null, error: err instanceof Error ? err.message : 'Unknown error' };
  }
}

export async function removeSessionVoiceSeat(
  seatId: string,
): Promise<{ error: string | null }> {
  try {
    const supabase = getSupabaseClient();
    const { error } = await supabase
      .from(SEATS)
      .update({ status: 'removed' })
      .eq('id', seatId);
    return { error: error?.message ?? null };
  } catch (err) {
    return { error: err instanceof Error ? err.message : 'Unknown error' };
  }
}

/** Invitable Voices: active, non-empty participation seats. */
export function isVoiceInvitable(voice: WorkspaceVoice): boolean {
  return voice.status === 'active' && voice.participationSeats.length > 0;
}
